import {
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  QueryList,
  ViewChild
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AccordionItemComponent } from '../../../lib/components/accordion/accordion-item/accordion-item.component';
import { MortgageApplicationNavigationEvents } from '../../../lib/constants/mortgage-application-navigation-events';
import { ApplicationHostTypeService } from '../../../lib/services/application-host-type.service';
import { ReferenceDataService } from '../../../lib/services/reference-data.service';
import { TabNavigationService } from '../../mortgage-application/services/tab-navigation.service';
import { MortgageApplicationTabNames } from '../../../lib/constants/mortgage-applications-tabs-names';

@Component({
  selector: 'boi-base-section-navigation',
  template: ``
})
export abstract class BaseSectionNavigationComponent implements OnDestroy {
  @ContentChildren(AccordionItemComponent, { descendants: true }) panelBarItems: QueryList<AccordionItemComponent>;

  @Output() public readonly detectChanges = new EventEmitter<any>();

  @ViewChild('container', { static: false }) container: ElementRef;

  protected dataSubscription: Subscription;

  private readonly destroySubject$ = new Subject();

  constructor(
    protected tabNavigationService: TabNavigationService,
    public router: Router,
    protected referenceDataService: ReferenceDataService,
    protected readonly applicationHostTypeService: ApplicationHostTypeService
  ) {
    this.router.events
      .pipe(
        filter((event: any) => event instanceof NavigationEnd || event?.routerEvent instanceof NavigationEnd),
        takeUntil(this.destroySubject$)
      )
      .subscribe((_) => {
        const intervalId = setInterval(() => {
          this.detectChanges.emit();
          if (this.panelBarItems) {
            this.panelBarItems.forEach((panelBarItem) => (panelBarItem.expanded = false));
            this.goToFirstIncompletedPanelBarItem();
            clearInterval(intervalId);
          }
        }, 50);
      });

    this.tabNavigationService.event
      .pipe(
        filter((event) => !!event),
        takeUntil(this.destroySubject$)
      )
      .subscribe((event) => {
        if (event.type === MortgageApplicationNavigationEvents.GoToSection) {
          this.goToSection(event.data['sectionTitleCode']);
        } else if (event.type === MortgageApplicationNavigationEvents.ChangeMortgageAppTab) {
          const { tab, index } = event.data;
          const scrollOptions: {} = { block: 'nearest', inline: 'center', behavior: 'smooth' };
          switch (tab) {
            case MortgageApplicationTabNames.YOUR_MORTGAGE:
              document.querySelector(`.${MortgageApplicationTabNames.YOUR_MORTGAGE}`)?.scrollIntoView(scrollOptions);
              break;
            case MortgageApplicationTabNames.APPLICANT:
              document
                .querySelectorAll(`.${MortgageApplicationTabNames.APPLICANT_TAB}`)
                [index]?.scrollIntoView(scrollOptions);
              break;
            case MortgageApplicationTabNames.YOUR_OUTGOINGS:
              document.querySelector(`.${MortgageApplicationTabNames.YOUR_OUTGOINGS}`)?.scrollIntoView(scrollOptions);
              break;
            case MortgageApplicationTabNames.YOUR_PROPERTY:
              document.querySelector(`.${MortgageApplicationTabNames.YOUR_PROPERTY}`)?.scrollIntoView(scrollOptions);
              break;
            default:
              return;
          }
        }
      });
  }

  protected abstract getNextTabRoute(): Array<string>;

  public goToFirstIncompletedPanelBarItem(): void {
    const firstIncompletedPanelBarItem =
      this.panelBarItems &&
      this.panelBarItems.find((panelBarItem) => panelBarItem.data && !panelBarItem.data['IsComplete']);
    if (firstIncompletedPanelBarItem) {
      firstIncompletedPanelBarItem.expanded = true;
    } else {
      this.panelBarItems.first.expanded = true;
    }
    this.detectChanges.emit();
  }

  public goToSection(sectionTitleCode: string): void {
    const sectionTitle = this.referenceDataService.getLocalizeLabel(sectionTitleCode);
    this.panelBarItems.forEach((panelBarItem) => (panelBarItem.expanded = false));
    const selectedPanelBar = this.panelBarItems.find((panelBarItem) => panelBarItem.title === sectionTitle);
    if (selectedPanelBar) {
      selectedPanelBar.expanded = true;
      this.detectChanges.emit();
      const section = document.querySelector(`.js-section-${sectionTitleCode.replace(/\s/g, '')}`);
      if (section) {
        this.container.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    }
  }

  public goToNextIncompleted(currentItem: AccordionItemComponent): void {
    const afterCurrentPanelBarItems = [];
    let isAfterCurrentItem = false;
    this.panelBarItems.forEach((panelBarItem) => {
      panelBarItem.expanded = false;
      if (isAfterCurrentItem) {
        afterCurrentPanelBarItems.push(panelBarItem);
      } else if (panelBarItem.title === currentItem.title) {
        isAfterCurrentItem = true;
      }
    });
    const nextIncompletPanelBarItem = afterCurrentPanelBarItems.find(
      (panelBarItem) => panelBarItem.data && !panelBarItem.data['IsComplete']
    );
    if (nextIncompletPanelBarItem) {
      nextIncompletPanelBarItem.expanded = true;
    } else if (afterCurrentPanelBarItems[0]) {
      afterCurrentPanelBarItems[0].expanded = true;
    } else if (this.getNextTabRoute().length > 0) {
      const nextRoute = this.getNextTabRoute();
      void this.router.navigate(nextRoute);
      let pageIndex = 3;
      let applicantIndex = 4;

      const interfaceTypeCode = this.applicationHostTypeService.getCode()
        ? this.applicationHostTypeService.getCode().toLowerCase()
        : undefined;
      if (nextRoute[1].toLowerCase() === interfaceTypeCode) {
        pageIndex++;
        applicantIndex++;
      }
      const additionalData = {
        tab: nextRoute[pageIndex]
      };
      if (nextRoute[applicantIndex] !== undefined) {
        additionalData['index'] = nextRoute[applicantIndex];
      }
      this.tabNavigationService.emitEvent({
        type: MortgageApplicationNavigationEvents.ChangeMortgageAppTab,
        data: additionalData
      });
    }
    setTimeout((_) => {
      this.container.nativeElement.scrollIntoView();
    }, 100);
  }

  public ngOnDestroy(): void {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
    this.destroySubject$.next();
    this.destroySubject$.complete();
  }
}
